import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { type Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system/styleFunctionSx';

import copyToClipboard from 'copy-to-clipboard';

import CheckmarkIcon from '@watershed/icons/components/Checkmark';
import CopyIcon from '@watershed/icons/components/Copy';
import mixinSx from '@watershed/style/mixinSx';

import Button, { ButtonProps } from './Button';
import IconButton from './IconButton';
import Tooltip, { type TooltipProps } from './Tooltip';

const checkmarkIconScaleInSx: SxProps<Theme> = {
  transformOrigin: 'center',
  animation: (theme) =>
    `copyButtonCheckmarkScaleIn ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} forwards`,
  '@keyframes copyButtonCheckmarkScaleIn': {
    from: { scale: 0.67 },
    to: { scale: 1 },
  },
};

type CopyButtonBaseProps = {
  icon?: React.ReactNode;
  iconOnly?: boolean;
  label?: LocalizedString;
  showTooltip?: boolean;
  onCopy?: () => void;
  tooltipTextOnCopy?: LocalizedString;
  tooltipPlacement?: TooltipProps['placement'];
  tooltipTimeout?: number;
  sx?: SxProps<Theme>;
} & Pick<ButtonProps, 'color' | 'size' | 'variant' | 'disabled'>;

type CopyButtonTextProps = CopyButtonBaseProps & {
  /** The text content to copy to clipboard. */
  text: LocalizedString;
  /** Optional MIME type format for the copy operation. */
  textFormat?: 'text/html' | 'text/plain';
  clipboardData?: never;
};

type CopyButtonClipboardDataProps = CopyButtonBaseProps & {
  /** A map of MIME types to string content, allowing multiple formats in a single copy. */
  clipboardData: Record<string, string>;
  text?: never;
  textFormat?: never;
};

type CopyButtonProps = CopyButtonTextProps | CopyButtonClipboardDataProps;

export default function CopyButton({
  color,
  size,
  icon,
  iconOnly = true,
  label = 'Copy',
  tooltipTextOnCopy = 'Copied to clipboard',
  showTooltip = true,
  tooltipTimeout = 1500,
  tooltipPlacement = 'bottom',
  onCopy,
  sx,
  text,
  textFormat,
  clipboardData,
  variant,
  disabled,
}: CopyButtonProps) {
  const { t } = useLingui();

  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [tooltipText, setTooltipText] = useState<string>(label);
  const minSize = size === 'large' ? 32 : 24;
  const iconSize = size === 'large' ? 16 : 12;
  const defaultIcon = copySuccess ? (
    // in the case of iconOnly, we don't just want the button to disappear
    // but if there is text (i.e. not iconOnly) then we are okay with the icon disappearing
    iconOnly ? (
      <CheckmarkIcon size={iconSize} sx={checkmarkIconScaleInSx} />
    ) : null
  ) : (
    <CopyIcon size={iconSize} />
  );

  useEffect(() => {
    if (copySuccess) {
      setTooltipText(tooltipTextOnCopy);
      const timer = setTimeout(() => {
        setCopySuccess(false);
        setTooltipText(label);
      }, tooltipTimeout);

      return () => clearTimeout(timer);
    }
  }, [copySuccess, tooltipTextOnCopy, label, tooltipTimeout]);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (clipboardData) {
      const items: Record<string, Blob> = {};
      for (const [mimeType, value] of Object.entries(clipboardData)) {
        items[mimeType] = new Blob([value], { type: mimeType });
      }
      await navigator.clipboard.write([new ClipboardItem(items)]);
    } else {
      copyToClipboard(text, { format: textFormat });
    }
    setCopySuccess(true);
    if (onCopy) onCopy();
  };

  const ariaLabelText = text ?? clipboardData?.['text/plain'] ?? label;
  const ariaLabel = t({
    message: `Copy ${ariaLabelText} to clipboard`,
    context: 'Button label to copy text to clipboard',
  });

  return (
    <Tooltip
      title={showTooltip ? tooltipText : ''}
      placement={tooltipPlacement}
    >
      {iconOnly ? (
        <IconButton
          aria-label={ariaLabel}
          color={color}
          onClick={handleClick}
          size={size}
          sx={mixinSx(
            { minWidth: minSize, minHeight: minSize },
            color === 'secondary' && {
              '& svg': {
                color: (theme) => theme.palette.grey50,
                transition: (theme) => theme.transitions.create('color'),
              },
              '&:hover svg': {
                color: (theme) => theme.palette.grey100,
              },
            },
            sx
          )}
          disabled={disabled}
        >
          {icon ?? defaultIcon}
        </IconButton>
      ) : (
        <Button
          color={color}
          startIcon={
            copySuccess ? (
              <CheckmarkIcon sx={checkmarkIconScaleInSx} />
            ) : (
              (icon ?? defaultIcon)
            )
          }
          aria-label={ariaLabel}
          onClick={handleClick}
          sx={sx}
          size={size}
          variant={variant}
          disabled={disabled}
        >
          {label}
        </Button>
      )}
    </Tooltip>
  );
}
